import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Include cookies (needed for admin session cookie) on every request.
// Safe globally: unauth endpoints just ignore the cookie.
axios.defaults.withCredentials = true;

const VOTER_KEY = 'ai_fw_voter';
export function getVoterId() {
  let id = localStorage.getItem(VOTER_KEY);
  if (!id) {
    id =
      'v_' +
      (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36));
    localStorage.setItem(VOTER_KEY, id);
  }
  return id;
}

// ---------------------------------------------------------------------------
// Admin auth — cookie-based (httpOnly `af_sess`). We keep a small "authed"
// FLAG (not the password) in localStorage so the SPA can render the right
// route synchronously without a network round-trip. The real auth lives in
// the httpOnly cookie which JS cannot read — XSS can't steal it.
// ---------------------------------------------------------------------------
const ADMIN_KEY = 'ai_fw_admin_authed';

export function getAdminToken() {
  // Compatibility shim — existing pages use `if (getAdminToken()) …` as a
  // "am I logged in?" check. Returns a truthy string when the flag is set,
  // null otherwise. Never returns the actual credential.
  return localStorage.getItem(ADMIN_KEY);
}

export function setAdminToken(t) {
  // Legacy signature — callers pass a truthy value to mark logged-in,
  // falsy to log out. We only ever store the boolean flag string.
  if (t) localStorage.setItem(ADMIN_KEY, '1');
  else localStorage.removeItem(ADMIN_KEY);
}

export function adminHeaders() {
  // No header needed with cookie auth. Kept for call-site compatibility.
  return {};
}

export async function adminLogin(password) {
  await axios.post(`${API}/admin/login`, { password });
  setAdminToken(true);
  return true;
}

export async function adminLogout() {
  try {
    await axios.post(`${API}/admin/logout`);
  } catch {
    /* ignore — clearing the local flag is enough */
  }
  setAdminToken(false);
}

// Homepage / listing
export const fetchHomepage = () => axios.get(`${API}/homepage`).then((r) => r.data);
export const fetchCategories = () => axios.get(`${API}/categories`).then((r) => r.data);
export const fetchTools = (params) =>
  axios.get(`${API}/tools`, { params }).then((r) => r.data);
export const fetchTool = (slug) =>
  axios.get(`${API}/tools/${slug}`).then((r) => r.data);

// Universal Resource API (Feb 2026)
export const fetchResources = (params) =>
  axios.get(`${API}/resources`, { params }).then((r) => r.data);
export const fetchResource = (slug) =>
  axios.get(`${API}/resources/${slug}`).then((r) => r.data);
export const fetchRelated = (slug, limit = 8) =>
  axios.get(`${API}/resources/${slug}/related`, { params: { limit } }).then((r) => r.data);
export const searchAutocomplete = (q, limit = 10) =>
  axios.get(`${API}/search/autocomplete`, { params: { q, limit } }).then((r) => r.data.results);
export const fetchDiscoveryIndex = () =>
  axios.get(`${API}/discovery`).then((r) => r.data);
export const fetchDiscoveryBucket = (slug, limit = 48) =>
  axios.get(`${API}/discovery/${slug}`, { params: { limit } }).then((r) => r.data);

// Redirect click URL. UI calls /api/go/:slug directly via <a href>.
export const goUrl = (slug, extraParams = {}) => {
  const qs = new URLSearchParams({
    ref: typeof window !== 'undefined' ? window.location.pathname : '',
    ...extraParams,
  }).toString();
  return `${API}/go/${slug}?${qs}`;
};

// Submissions (from previous iteration, unchanged)
export async function fetchSubmissions() {
  const { data } = await axios.get(`${API}/submissions`, {
    params: { voter_id: getVoterId() },
  });
  return data;
}
export async function createSubmission(payload) {
  const { data } = await axios.post(`${API}/submissions`, payload);
  return data;
}
export async function voteSubmission(id, value) {
  const { data } = await axios.post(`${API}/submissions/${id}/vote`, {
    voter_id: getVoterId(),
    value,
  });
  return data;
}

// Admin
export async function adminUpdateTool(slug, patch) {
  const { data } = await axios.patch(`${API}/admin/tools/${slug}`, patch, {
    headers: adminHeaders(),
  });
  return data;
}
export async function adminAnalytics(days = 30) {
  const { data } = await axios.get(`${API}/admin/analytics`, {
    params: { days },
    headers: adminHeaders(),
  });
  return data;
}

// Admin: Universal Resource management (Feb 2026)
export async function adminCreateResource(payload) {
  const { data } = await axios.post(`${API}/admin/resources`, payload, { headers: adminHeaders() });
  return data;
}
export async function adminUpdateResource(slug, patch) {
  const { data } = await axios.patch(`${API}/admin/resources/${slug}`, patch, { headers: adminHeaders() });
  return data;
}
export async function adminDeleteResource(slug) {
  const { data } = await axios.delete(`${API}/admin/tools/${slug}`, { headers: adminHeaders() });
  return data;
}
export async function adminBulkImport(items, upsert = true) {
  const { data } = await axios.post(`${API}/admin/resources/import`, { items, upsert }, { headers: adminHeaders() });
  return data;
}
export function adminExportUrl(type, format = 'json') {
  const params = new URLSearchParams();
  if (type) params.set('type', type);
  params.set('format', format);
  return `${API}/admin/resources/export?${params.toString()}`;
}
export async function adminExport(type, format = 'json') {
  const res = await axios.get(`${API}/admin/resources/export`, {
    params: { type, format },
    headers: adminHeaders(),
    responseType: format === 'csv' ? 'text' : 'json',
  });
  return res.data;
}
export async function adminResourceVersions(slug) {
  const { data } = await axios.get(`${API}/admin/resources/${slug}/versions`, { headers: adminHeaders() });
  return data;
}
export async function adminRollback(slug, version) {
  const { data } = await axios.post(`${API}/admin/resources/${slug}/rollback/${version}`, {}, { headers: adminHeaders() });
  return data;
}
export async function adminFindDuplicates() {
  const { data } = await axios.get(`${API}/admin/duplicates`, { headers: adminHeaders() });
  return data;
}
export async function adminBulkEdit(slugs, updates) {
  const { data } = await axios.post(`${API}/admin/resources/bulk-edit`, { slugs, updates }, { headers: adminHeaders() });
  return data;
}
export async function adminBulkTag(slugs, tags, remove = false) {
  const { data } = await axios.post(`${API}/admin/resources/bulk-tag`, { slugs, tags, remove }, { headers: adminHeaders() });
  return data;
}
export async function adminMerge(primarySlug, duplicateSlugs) {
  const { data } = await axios.post(`${API}/admin/resources/merge`, {
    primary_slug: primarySlug,
    duplicate_slugs: duplicateSlugs,
  }, { headers: adminHeaders() });
  return data;
}

// Monetization: submissions + newsletter (Feb 2026)
export async function fetchSubmissionTiers() {
  const { data } = await axios.get(`${API}/submissions/tiers`);
  return data.tiers;
}
export async function submitTool(payload) {
  const { data } = await axios.post(`${API}/submit-tool`, payload);
  return data;
}
export async function pollCheckoutStatus(sessionId) {
  const { data } = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
  return data;
}
export async function subscribeNewsletter(email, source = 'homepage') {
  const { data } = await axios.post(`${API}/newsletter/subscribe`, { email, source });
  return data;
}

// Contact form
export async function submitContact(payload) {
  const { data } = await axios.post(`${API}/contact`, payload);
  return data;
}

// Alternatives
export async function fetchAlternatives(slug, limit = 24) {
  const { data } = await axios.get(`${API}/resources/${slug}/alternatives`, { params: { limit } });
  return data;
}

// Public config
export async function fetchResourceTypes() {
  const { data } = await axios.get(`${API}/resource-types`);
  return data;
}
export async function fetchFeatureFlags() {
  const { data } = await axios.get(`${API}/feature-flags`);
  return data.flags;
}
export async function universalSearch(params) {
  const { data } = await axios.get(`${API}/search`, { params });
  return data;
}
